import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { IAlert } from '../../models/alert.model';
import { IRecordingsPopupFilter, RECORDINGS_POPUP_WINDOW_NAME } from '../../models/recordings-popup.model';

const RECORDINGS_LEAD_IN_MS = 30000;

// Owns the Investigate popup's window handle, which cannot live in the store because a
// Window is not serialisable. Called only from RecordingsEffects.
@Injectable({
  providedIn: 'root'
})
export class InvestigateService {

  private location = inject(Location);
  private popupWindow: Window | null = null;

  buildPopupFilter(alert: IAlert): IRecordingsPopupFilter | null {
    const isoTimestamp = alert.creation_timestamp
      .replace(' ', 'T')
      .replace(/(\.\d{3})\d+$/, '$1');
    const startDate = new Date(isoTimestamp);
    if (Number.isNaN(startDate.getTime())) {
      return null;
    }
    startDate.setTime(startDate.getTime() - RECORDINGS_LEAD_IN_MS);

    return {
      siteIds: [alert.site_id],
      cameraId: alert.camera_id,
      startTimestamp: startDate.toISOString(),
    };
  }

  // Opening by window name reuses an existing popup (navigating it) instead of spawning another.
  openPopup(): boolean {
    this.popupWindow = window.open(this.location.prepareExternalUrl('/recordings'), RECORDINGS_POPUP_WINDOW_NAME, 'popup=true');
    this.popupWindow?.focus();
    return this.popupWindow != null;
  }

  isPopupOpen(): boolean {
    return this.popupWindow != null && !this.popupWindow.closed;
  }

  // The popup runs the full app, so the operator can navigate it away from recordings, where a
  // pushed filter has nothing to apply it.
  isPopupOnRecordings(): boolean {
    const popupWindow = this.popupWindow;
    if (popupWindow == null || popupWindow.closed) {
      return false;
    }

    try {
      return popupWindow.location.pathname === this.location.prepareExternalUrl('/recordings');
    } catch {
      // Reading location throws once the popup is on another origin; reopening navigates it back.
      return false;
    }
  }

  focusPopup(): void {
    this.popupWindow?.focus();
  }

  isPopupWindow(): boolean {
    return window.name === RECORDINGS_POPUP_WINDOW_NAME;
  }
}
